import React from 'react';

import { useAutoNavigate } from '@betterment/js-runtime';
import { Box, Button, H1, IconButton, PancakeStack, WorkflowHeader } from '@betterment/samba-react';
import { FormikValidateAfterSubmit } from '@betterment/shared-components';
import { Form } from 'formik';
import * as Yup from 'yup';

import { AgreementFileSection } from './AgreementFileSection';
import { AgreementMethodSection } from './AgreementMethodSection';
import { uploadClientAgreement } from './api';

import urlBuilder from '../../../Shared/url-builder';

type AgreementMethod = 'docusign' | 'checkbox_consent';

export type ReplaceClientAgreementFormValues = {
  pdfFile: File | null;
  documentName: string;
  useUploadedPdfName: boolean;
  agreementMethod: AgreementMethod;
};

const DEFAULT_VALUES: ReplaceClientAgreementFormValues = {
  pdfFile: null,
  documentName: '',
  useUploadedPdfName: false,
  agreementMethod: 'checkbox_consent',
};

const MAX_FILE_SIZE_BYTES = 500 * 1024 * 1024;

const Schema = Yup.object().shape({
  pdfFile: Yup.mixed<File>()
    .required('PDF file is required')
    .test('fileSize', 'File must be 500 MB or less', file => file === undefined || file.size <= MAX_FILE_SIZE_BYTES),
  documentName: Yup.string()
    .max(255, 'Document name must be 255 characters or less')
    .when('useUploadedPdfName', {
      is: false,
      then: schema => schema.required('Document name is required'),
      otherwise: schema => schema,
    }),
  useUploadedPdfName: Yup.boolean(),
  agreementMethod: Yup.string<AgreementMethod>().required('Agreement method is required'),
});

export const ReplaceClientAgreementPage: React.FC = () => {
  const navigate = useAutoNavigate();

  const handleSubmit = async (values: ReplaceClientAgreementFormValues) => {
    if (!values.pdfFile) return;

    const name = values.documentName || undefined;
    const result = await uploadClientAgreement(values.pdfFile, name);

    if (result.success) {
      void navigate(urlBuilder.getClientAgreementSignaturePage.absolutePath);
    }
  };

  return (
    <PancakeStack css={{ maxWidth: '100%', minHeight: '100vh' }}>
      <PancakeStack.Header
        css={{
          maxWidth: '100%',
          display: 'flex',
          justifyContent: 'center',
          borderBottomStyle: 'solid',
          borderBottomWidth: 'thin',
          borderColor: 'border',
        }}
      >
        <WorkflowHeader css={{ width: '100%' }}>
          <WorkflowHeader.Leading />
          <WorkflowHeader.Title>Replace agreement</WorkflowHeader.Title>
          <WorkflowHeader.Trailing>
            <IconButton
              name="X"
              label="Close"
              spacing="compact"
              onClick={() => void navigate(urlBuilder.getClientAgreementSignaturePage.absolutePath)}
            />
          </WorkflowHeader.Trailing>
        </WorkflowHeader>
      </PancakeStack.Header>

      <PancakeStack.Main css={{ maxWidth: '100%' }}>
        <Box
          css={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'space.400',
            maxWidth: '648px',
            mx: 'auto',
            py: 'space.600',
            px: 'space.200',
            width: '100%',
          }}
        >
          <H1>Replace client agreement.</H1>

          <FormikValidateAfterSubmit<ReplaceClientAgreementFormValues>
            onSubmit={handleSubmit}
            initialValues={DEFAULT_VALUES}
            validationSchema={Schema}
          >
            {({ isSubmitting }) => (
              <Form>
                <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.400' }}>
                  <AgreementFileSection />
                  <AgreementMethodSection />
                  <Box>
                    <Button type="submit" variant="solidPrimary" disabled={isSubmitting}>
                      Submit
                    </Button>
                  </Box>
                </Box>
              </Form>
            )}
          </FormikValidateAfterSubmit>
        </Box>
      </PancakeStack.Main>
    </PancakeStack>
  );
};
